import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, CreditCard, Loader2, Copy, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

function PixQr({ text }) {
  return (
    <div className="bg-white p-3 rounded-sm border border-border w-fit" data-testid="pix-qr-svg">
      <QRCodeSVG value={text} size={200} level="M" />
    </div>
  );
}

export default function SubscriptionPage() {
  const { refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(null);
  const [pixCharge, setPixCharge] = useState(null);
  const [pixPolling, setPixPolling] = useState(false);
  const [params, setParams] = useSearchParams();
  const canceled = params.get("canceled");
  const presetPlan = params.get("plan");

  const loadAll = async () => {
    try {
      const [p, s] = await Promise.all([api.get("/subscription/plans"), api.get("/subscription/me")]);
      setPlans(p.data);
      setSub(s.data);
    } catch (_) { /* noop */ }
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    if (canceled) { toast.info("Pagamento cancelado."); setParams({}, { replace: true }); }
    // eslint-disable-next-line
  }, [canceled]);

  const pixCreate = async (planId) => {
    setLoading(planId);
    try {
      const { data } = await api.post("/payments/pix/create", { plan: planId });
      setPixCharge(data);
      pollPix(data.charge_id);
    } catch (_) { toast.error("Falha ao gerar PIX."); }
    setLoading(null);
  };

  const pollPix = (chargeId) => {
    setPixPolling(true);
    let attempts = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/pix/${chargeId}/status`);
        if (data.status === "approved") {
          toast.success("Pagamento PIX confirmado!");
          await loadAll(); await refresh();
          setPixCharge(null); setPixPolling(false);
          return;
        }
        attempts += 1;
        if (attempts < 30) setTimeout(tick, 5000);
        else setPixPolling(false);
      } catch (_) { setPixPolling(false); }
    };
    setTimeout(tick, 4000);
  };

  const confirmMock = async () => {
    if (!pixCharge) return;
    try {
      await api.post(`/payments/pix/${pixCharge.charge_id}/confirm-mock`);
      toast.success("Plano ativado (modo demo).");
      await loadAll(); await refresh();
      setPixCharge(null); setPixPolling(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro."); }
  };

  const copyPixCode = () => {
    if (!pixCharge?.qr_code) return;
    navigator.clipboard.writeText(pixCharge.qr_code);
    toast.success("Código PIX copiado.");
  };

  return (
    <div className="space-y-8" data-testid="subscription-page">
      <div>
        <div className="overline text-accent mb-3">Assinatura</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Seu plano de acompanhamento</h1>
      </div>

      {sub?.status === "paused" && (
        <div className="bg-warning/10 border border-warning/30 rounded-sm p-4" data-testid="paused-subscription">
          <div className="font-semibold text-foreground">Plano pausado</div>
          <div className="text-sm text-muted-foreground mt-1">Seu plano foi pausado temporariamente pela equipe clínica. Entre em contato para reativar.</div>
        </div>
      )}

      {sub?.status === "active" && (
        <div className="bg-card border border-success/30 bg-success/5 rounded-sm p-6" data-testid="active-subscription">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-sm bg-success text-white grid place-items-center flex-none">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <div className="overline text-success mb-1">Plano ativo</div>
              <div className="font-heading text-xl font-semibold capitalize">{sub.plan}</div>
              {sub.next_billing && <div className="text-xs text-muted-foreground mt-1">Próxima renovação: {new Date(sub.next_billing).toLocaleDateString("pt-BR")}</div>}
              <div className="text-xs text-muted-foreground mt-2">
                Pagamento: {sub.payment_method === "pix-mock" ? "PIX (demo)" : sub.payment_method === "pix" ? "PIX" : sub.mocked ? "Demo manual" : "Stripe"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PIX QR display */}
      {pixCharge && (
        <div className="bg-card border border-primary rounded-sm p-6" data-testid="pix-charge-display">
          <div className="flex items-start gap-3 mb-4">
            <QrCode className="w-5 h-5 text-primary" />
            <div>
              <div className="overline text-primary mb-1">PIX gerado</div>
              <div className="font-heading font-semibold">R$ {pixCharge.amount.toFixed(2)} — Plano {pixCharge.plan}</div>
              {pixCharge.mocked && <div className="text-xs text-warning mt-1">Modo demo (MP_ACCESS_TOKEN não configurado)</div>}
            </div>
          </div>
          {pixCharge.qr_code_base64 ? (
            <img src={`data:image/png;base64,${pixCharge.qr_code_base64}`} alt="QR PIX" className="w-48 h-48 border border-border rounded-sm" data-testid="pix-qr-image" />
          ) : (
            <PixQr text={pixCharge.qr_code} />
          )}
          <div className="mt-4 p-3 bg-muted rounded-sm">
            <div className="overline text-muted-foreground mb-1">Código copia-e-cola</div>
            <div className="text-xs font-mono break-all" data-testid="pix-code-text">{pixCharge.qr_code}</div>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={copyPixCode} className="inline-flex items-center gap-2 rounded-sm border border-border bg-card px-3 py-2 text-sm font-semibold hover:bg-muted transition-colors" data-testid="copy-pix-btn">
              <Copy className="w-4 h-4" /> Copiar código
            </button>
            {pixCharge.mocked && (
              <button onClick={confirmMock} className="rounded-sm bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="pix-confirm-mock-btn">
                Confirmar pagamento (demo)
              </button>
            )}
            {!pixCharge.mocked && (
              <span className="inline-flex items-center gap-2 text-xs text-muted-foreground" data-testid="pix-await-admin">
                <Loader2 className="w-3 h-3 animate-spin" /> Após pagar, o farmacêutico ativará seu plano em minutos.
              </span>
            )}
            {pixPolling && <span className="inline-flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Aguardando confirmação…</span>}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {plans.map((p) => {
          const isActive = sub?.plan === p.id && sub?.status === "active";
          const isRecommended = p.id === "premium";
          return (
            <div
              key={p.id}
              className={`border rounded-sm p-8 bg-card ${isRecommended ? "border-2 border-primary" : "border-border"} ${presetPlan === p.id ? "ring-2 ring-primary/50" : ""}`}
              data-testid={`sub-plan-${p.id}`}
            >
              {isRecommended && !isActive && (
                <div className="inline-block bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-sm mb-3">RECOMENDADO</div>
              )}
              <div className="overline text-muted-foreground mb-2">{p.name}</div>
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-5xl font-bold">R$ {p.price.toFixed(0)}</span>
                <span className="text-muted-foreground">/mês</span>
              </div>
              <ul className="mt-6 space-y-3 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-success mt-0.5" /> {f}</li>
                ))}
              </ul>
              <button
                onClick={() => pixCreate(p.id)}
                disabled={isActive || loading !== null}
                className={`mt-8 w-full rounded-sm px-4 py-3 text-sm font-semibold transition-colors ${isActive ? "bg-muted text-muted-foreground" : "bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60"}`}
                data-testid={`subscribe-${p.id}-btn`}
              >
                {isActive ? "Plano atual" : loading === p.id ? "Processando…" : "Gerar PIX"}
              </button>
            </div>
          );
        })}
      </div>

      <div className="rounded-sm border border-dashed border-border bg-muted/40 p-5 text-sm text-muted-foreground flex items-start gap-3">
        <CreditCard className="w-5 h-5 mt-0.5" />
        <div>
          <div className="font-semibold text-foreground">Pagamento seguro</div>
          PIX direto na conta do farmacêutico — escaneie o QR ou copie/cole o código no app do seu banco. Após o pagamento, o farmacêutico ativa seu plano em minutos.
        </div>
      </div>
    </div>
  );
}
